import axios from 'axios';

const api = axios.create({
  baseURL: `/api`,
  withCredentials: true,
});

// Public auth-entry endpoints must never carry a token. DRF's TokenAuthentication
// rejects an *invalid* token with 401 at the auth layer — before AllowAny is checked —
// so a stale token in localStorage would otherwise 401 even login/register/password-reset,
// locking the user out with no way back in (they can't log in to get a fresh token).
const NO_AUTH_PATHS = [
  '/auth/login/',
  '/auth/register/',
  '/auth/password-reset/',
  '/auth/password-reset/confirm/',
  '/auth/tiers/',
];

// Attach token to every request if present (except the public auth-entry endpoints)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const isNoAuth = NO_AUTH_PATHS.some((p) => (config.url || '').startsWith(p));
  if (token && !isNoAuth) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// If a request comes back 401, the token is invalid/expired. Drop it so it stops
// poisoning subsequent requests, and let the app reset auth state to logged-out.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return Promise.reject(error);
  },
);

// Auth
export const login = (data) => api.post('/auth/login/', data);
export const register = (data) => api.post('/auth/register/', data);
export const getMe = (token) => api.get('/auth/me/', { headers: { Authorization: `Token ${token}` } });
export const logout = (token) => api.post('/auth/logout/', null, { headers: { Authorization: `Token ${token}` } });
export const updateProfile = (data) => api.patch('/auth/profile/', data);
export const changePassword = (data) => api.post('/auth/change-password/', data);
export const requestPasswordReset = (data) => api.post('/auth/password-reset/', data);
export const confirmPasswordReset = (data) => api.post('/auth/password-reset/confirm/', data);
export const getMembershipTiers = () => api.get('/auth/tiers/');
export const getUsageSummary = () => api.get('/usage/');
export const createCheckoutSession = (data) => api.post('/auth/create-checkout-session/', data);
export const getSubscription = () => api.get('/auth/subscription/');
export const cancelSubscription = () => api.post('/auth/subscription/cancel/');
export const resumeSubscription = () => api.post('/auth/subscription/resume/');
export const getInvoices = () => api.get('/auth/invoices/');
export const createSetupIntentSession = () => api.post('/auth/setup-intent-session/');

// Strategies
export const getStrategies = () => api.get('/strategies/');
export const getStrategy = (id) => api.get(`/strategies/${id}/`);
export const createStrategy = (data) => api.post('/strategies/', data);
export const duplicateStrategy = (id, data) => api.post(`/strategies/${id}/duplicate/`, data);
export const getStrategyPaperSessions = (id) => api.get(`/strategies/${id}/paper_sessions/`);
export const updateStrategy = (id, data) => api.put(`/strategies/${id}/`, data);
export const deleteStrategy = (id) => api.delete(`/strategies/${id}/`);
export const explainStrategy = (id) => api.post(`/strategies/${id}/explain/`);
export const explainStrategyBrief = (id) => api.post(`/strategies/${id}/explain-brief/`);
export const generateStrategy = (data) => api.post('/strategies/generate/', data);
// Background analyst→builder generation: enqueues a Celery task, returns 202 with
// {status:'building', gen_id, notification_id, chat_id}. Result is persisted to the
// chat + a completion notification, so it survives navigating away.
export const generateStrategyAsync = (data) => api.post('/strategies/generate_async/', data);

// Export a strategy graph to external-platform code (backtrader / PineScript).
// Pass one of: { strategy_id }, { backtest_id }, or { graph }, plus { format }.
export const exportStrategy = (data) => api.post('/export/', data);
export const getExportFormats = () => api.get('/export/formats/');

// Backtests
export const getBacktests = (params) => api.get('/backtests/', { params });
export const getRecentBacktestsSummary = () => api.get('/backtests/recent-summary/');
export const getComparableBacktests = (params) => api.get('/backtests/comparable/', { params });
export const getHomeMarketNews = (limit = 6) => api.get('/home/news/', { params: { limit } });
export const getHomeMarketOverview = () => api.get('/home/overview/');
export const getHomeMarketInsight = () => api.get('/home/insight/');
export const getHomeMarketRegime = (days = 365) => api.get('/home/regime/', { params: { days } });
export const getHomeEconomicCalendar = () => api.get('/home/economic-calendar/');
export const getStockDetail = (ticker, { period = '6mo', assetType = 'auto' } = {}) =>
  api.get(`/stock/${ticker}/`, { params: { period, asset_type: assetType } });
export const getBacktest = (id) => api.get(`/backtests/${id}/`);
export const getBacktestProgress = (id) => api.get(`/backtests/${id}/progress/`);
export const createBacktest = (data) => api.post('/backtests/', data);
export const runBacktest = (id, data) => api.post(`/backtests/${id}/run/`, data || {});
export const runWalkForward = (id, data) => api.post(`/backtests/${id}/walk-forward/`, data || {});
export const optimizeWalkForward = (id, data) => api.post(`/backtests/${id}/optimize-wfa/`, data || {});
export const runParamSweep = (id, data) => api.post(`/backtests/${id}/param-sweep/`, data || {});
export const improveBacktest = (id, data) => api.post(`/backtests/${id}/improve/`, data || {});
export const suggestBacktestFixes = (id) => api.post(`/backtests/${id}/suggest_fixes/`);
export const getPriceRange = (id, start, end) => api.get(`/backtests/${id}/price-range/`, { params: { start, end } });
// Results-page "Download data": catalog of CSV datasets, then the file itself
// (one CSV, or a ZIP when several datasets are picked).
export const getBacktestDataCatalog = (id) => api.get(`/backtests/${id}/export-data/`);
export const downloadBacktestData = (id, datasets) =>
  api.get(`/backtests/${id}/export-data/`, { params: { datasets: datasets.join(',') }, responseType: 'blob' });

// Baskets (watchlists) + multi-symbol basket runs
export const getSymbolGroups = () => api.get('/symbol-groups/');
export const createSymbolGroup = (data) => api.post('/symbol-groups/', data);
export const updateSymbolGroup = (id, data) => api.patch(`/symbol-groups/${id}/`, data);
export const deleteSymbolGroup = (id) => api.delete(`/symbol-groups/${id}/`);
export const getSymbolGroupQuotes = (id) => api.get(`/symbol-groups/${id}/quotes/`);
export const createBasketRun = (data) => api.post('/basket-runs/', data);
export const getBasketRun = (id) => api.get(`/basket-runs/${id}/`);
// `classes` (optional CSV, e.g. 'stock,fx') restricts which asset classes the
// resolver may return — used by surfaces that auto-detect (asset_type='auto')
// but can't run every class (the runner excludes crypto).
export const validateSymbol = (symbol, assetType = 'stock', classes) =>
  api.get('/validate-symbol/', { params: { symbol, asset_type: assetType, ...(classes ? { classes } : {}) } });

// Live typeahead for ticker-entry surfaces — returns ranked {symbol, asset_type,
// quote_type, name, exchange} suggestions as the user types. `classes` (optional
// CSV) restricts asset classes the same way validateSymbol does.
export const searchSymbols = (q, classes) =>
  api.get('/symbol-search/', { params: { q, ...(classes ? { classes } : {}) } });

// AI Analyst
export const analystChat = (data) => api.post('/analyst/chat/', data);
export const getAnalystChats = () => api.get('/analyst/chats/');

// Notifications
export const getNotifications = () => api.get('/auth/notifications/');
export const markNotificationRead = (id) => api.patch(`/auth/notifications/${id}/read/`);
export const markAllNotificationsRead = () => api.post('/auth/notifications/mark-all-read/');
export const deleteNotification = (id) => api.delete(`/auth/notifications/${id}/`);

// Messages
export const getMessageThreads = () => api.get('/auth/messages/');
export const getMessageThread = (id) => api.get(`/auth/messages/${id}/`);
export const replyToMessageThread = (id, message) => api.post(`/auth/messages/${id}/reply/`, { message });

export const getAnalystChat = (id) => api.get(`/analyst/chats/${id}/`);
export const updateAnalystChat = (id, data) => api.patch(`/analyst/chats/${id}/update/`, data);
export const deleteAnalystChat = (id) => api.delete(`/analyst/chats/${id}/delete/`);

// Builder Chat (conversational AI strategy builder)
export const builderChatSend = (data) => api.post('/builder-chat/', data);
export const getBuilderChats = () => api.get('/builder-chats/');
export const getBuilderChat = (id) => api.get(`/builder-chats/${id}/`);
export const updateBuilderChat = (id, data) => api.patch(`/builder-chats/${id}/update/`, data);
export const deleteBuilderChat = (id) => api.delete(`/builder-chats/${id}/delete/`);
export const saveBuilderChatStrategy = (id, data) => api.post(`/builder-chats/${id}/save/`, data || {});

// Feedback
export const submitFeedback = (formData) => api.post('/auth/feedback/', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});

export const submitContact = (data) => api.post('/auth/contact/', data);

// Paper Trading
export const getPaperTradingSessions = (params) => api.get('/paper-trading/', { params });
export const getPaperTradingSession = (id) => api.get(`/paper-trading/${id}/`);
export const deletePaperTradingSession = (id) => api.delete(`/paper-trading/${id}/`);
export const createPaperTradingSession = (data) => api.post('/paper-trading/', data);
export const pausePaperTradingSession = (id) => api.post(`/paper-trading/${id}/pause/`);
export const resumePaperTradingSession = (id) => api.post(`/paper-trading/${id}/resume/`);
export const stopPaperTradingSession = (id) => api.post(`/paper-trading/${id}/stop/`);
export const getPaperTradingPositions = (id) => api.get(`/paper-trading/${id}/positions/`);
export const getPaperTradingTrades = (id) => api.get(`/paper-trading/${id}/trades/`);
export const updatePaperTradingSettings = (id, data) => api.patch(`/paper-trading/${id}/update-settings/`, data);

// Notification Preferences
export const getNotificationPreferences = () => api.get('/auth/notification-preferences/');
export const updateNotificationPreferences = (data) => api.patch('/auth/notification-preferences/update/', data);

// Blog
export const getBlogPosts = () => api.get('/blog/posts/');
export const getBlogPost = (slug) => api.get(`/blog/posts/${slug}/`);

export default api;
